import Stack from '@mui/material/Stack'
import { useEffect, useMemo } from 'react'
import {
  Path,
  PathValue,
  UseControllerProps,
  useFormContext,
} from 'react-hook-form'
import { useParams } from 'react-router-dom'

import { useListHistory } from '@/hooks'
import { useListCustomComponent } from '@/screens/ComponentLibrary/hooks'
import { ComponentType, ReleaseBundleInput, Visibility } from '@/utils/api'

import { Option, VailSelect } from '../reactHookForm'

export type ComponentSelectorProps<T extends object> = Pick<
  UseControllerProps<T>,
  'control' | 'name'
> & {
  type: ComponentType
  optional?: boolean
}
export function ComponentSelector<T extends object>(
  props: ComponentSelectorProps<T>
) {
  const { optional = false } = props
  const { orgID } = useParams()
  const form = useFormContext<T>()
  const { name, type } = (form.watch(props.name) ?? {}) as {
    name: ReleaseBundleInput & { componentName: string }
    type: ComponentType
  }
  const components = useListCustomComponent(
    orgID,
    {
      type: { eq: props.type as ComponentType },
      visibility: { ne: Visibility.archived },
    },
    [type]
  )
  const historyItem = useListHistory(
    ['history', name as unknown as string, 'DEBUG'],
    'customComponent',
    { id: name as unknown as string }
  )
  const versionOptions: Option[] =
    historyItem.data?.map((history) => ({
      label: history.name,
      value: history.id,
    })) ?? []
  const nameOptions: Option[] = useMemo(
    () =>
      components.data?.map((component) => ({
        label: component.name,
        value: component.id,
      })) ?? [],
    [components.data]
  )

  const nameLabel = form.watch(`${props.name}.name` as Path<T>)
  const { setValue } = form
  useEffect(() => {
    const selected = nameOptions.find(
      (opt) => typeof opt !== 'string' && opt.value === nameLabel
    )
    if (!selected) return
    setValue(
      `${props.name}.componentName` as Path<T>,
      (typeof selected === 'string' ? selected : selected?.label) as PathValue<
        T,
        Path<T>
      >
    )
  }, [nameLabel, nameOptions, props.name, setValue])

  return (
    <Stack columnGap={4} direction="row" minWidth="fit-content">
      <VailSelect
        control={form.control}
        helperText={
          !nameOptions.length && components.isSuccess
            ? 'No components found'
            : undefined
        }
        label="Component Name"
        name={`${props.name}.name` as Path<T>}
        options={nameOptions}
        rules={optional ? {} : undefined}
        sx={{ minWidth: 200 }}
        withNone={optional}
        onSelect={() => form.clearErrors()}
      />
      <VailSelect
        control={form.control}
        disabled={!name}
        label="Version"
        name={`${props.name}.version` as Path<T>}
        options={versionOptions}
        rules={optional ? {} : undefined}
        sx={{ minWidth: 200 }}
      />
    </Stack>
  )
}
